<template>
  <div>
    <div class="card m-10 mb-0" v-if="project && !project.approved && project.status !== 'CANCELLED'">
      <div class="card__description p-0 d-flex-responsive">
        <div class="full-width p-20" v-if="!isModerator && !isAdmin">
          <p class="font-weight-bold">{{ $t('messages.approval_text') }}</p>
          <br />
          <p>{{ $t('messages.approval_text_pre') }}</p>
          <br />
          <ul>
            <li>{{ $t('messages.approval_text_req_1') }}</li>
            <li>{{ $t('messages.approval_text_req_2') }}</li>
            <li>{{ $t('messages.approval_text_req_3') }}</li>
            <li>{{ $t('messages.approval_text_req_4') }}</li>
            <li>{{ $t('messages.approval_text_req_5') }}</li>
            <li>{{ $t('messages.approval_text_req_6') }}</li>
          </ul>
          <p>{{ $t('messages.approval_text_post') }}</p>
        </div>
        <div class="full-width pl-20 pr-20 pb-20 pt-20-r" v-if="!isModerator && !isAdmin">
          <div class="pb-20 card__scroll card__scroll__small">
            <p class="font-weight-bold mb-2">{{ $t('messages.approval_feedback_moderator') }}:</p>
            <div v-if="project.feedback.length > 0" class="mb-3">
              <div v-for="feedback in project.feedback" :key="'fb' + feedback.id" class="mb-3">
                <div>
                  <b-link :to="{ path: '/user/' + slugText(feedback.user_id + '-' + feedback.user_full_name) }">{{ feedback.user_full_name }}</b-link>
                  <div class="font-italic">{{ translateDate(feedback.created_date) }}</div>
                  <div class="pt-3" v-html="feedback.feedback"></div>
                </div>
              </div>
            </div>
            <p class="font-italic" v-if="project.feedback.length === 0">{{ $t('messages.approval_feedback_none') }}</p>
          </div>
          <div class="d-flex">
            <b-form @submit.prevent="requestApproval">
              <click-confirm button-size="sm" :messages="confirmMessage" v-if="!project.to_approve">
                <notification-button v-b-tooltip.hover :title="$t('messages.approval_request_exp')" :message="$t('messages.approval_request')"
                                     :notification="notifications.approvalRequest" field="approvalRequest"></notification-button>
              </click-confirm>
              <b-button v-if="project.to_approve" variant="primary" :title="$t('messages.approval_request_wait')" :disabled="true">{{ $t('messages.approval_request_wait') }}</b-button>
            </b-form>
          </div>
        </div>
        <div class="full-width p-20" v-if="isModerator || isAdmin">
          <div v-if="$store.state.userId != project.user_id">
            <b-form-group id="feedbackGroup"
                          :label="$t('messages.approval_feedback')+':'"
                          label-for="feedback"
                          :invalid-feedback="$t('errors.approval_feedback')">
              <b-form-input id="feedback" name="feedback"
                            v-model="approval.feedback"
                            :disabled="!project.to_approve && project.feedback.length > 0"
                            @input="$v.approval.feedback.$touch"
                            :state="$v.approval.feedback.$dirty ? !$v.approval.feedback.$error : null"></b-form-input>
            </b-form-group>
          </div>
          <div class="d-flex">
            <b-form @submit.prevent="sendFeedback" v-if="$store.state.userId != project.user_id">
              <notification-button :message="(!project.to_approve && project.feedback.length > 0) ? $t('messages.approval_feedback_sent') : $t('messages.approval_feedback_send')"
                                   :notification="notifications.approvalFeedback" field="approvalFeedback"
                                   :disabled="$v.approval.$invalid" class="mr-2"></notification-button>
            </b-form>
            <b-form @submit.prevent="approve">
              <click-confirm button-size="sm" :messages="confirmMessage">
                <notification-button :message="$t('messages.approval_approve')"
                                     :notification="notifications.approval" field="approval"></notification-button>
              </click-confirm>
            </b-form>
          </div>
        </div>
        <div class="full-width pl-20 pr-20 pb-20 pt-20-r" v-if="isModerator || isAdmin">
          <div>
            <div class="card__scroll card__scroll__small">
              <div v-if="project.feedback.length > 0" class="full-width">
                <p class="font-weight-bold mb-2">{{ $t('messages.approval_feedback_previous') }}:</p>
                <div v-for="feedback in project.feedback" :key="'fb' + feedback.id" class="mb-3">
                  <div>
                    <b-link :to="{ path: '/user/' + slugText(feedback.user_id + '-' + feedback.user_full_name) }">{{ feedback.user_full_name }}</b-link>
                    <div class="font-italic">{{ translateDate(feedback.created_date) }}</div>
                    <div class="pt-3" v-html="feedback.feedback"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { projectService } from '../../services/project.service'
import NotificationButton from '../NotificationButton'

import { defaultMixin } from '../../mixin/default'
import { notificationMixin } from '../../mixin/notification'
import { validationMixin } from 'vuelidate'

import { required } from 'vuelidate/lib/validators'

export default {
  name: 'approval',
  mixins: [ defaultMixin, notificationMixin, validationMixin ],
  components: { NotificationButton },
  props: {
    value: Object
  },
  data: function () {
    return {
      approval: {
        feedback: null
      },
      project: this.value,
      notifications: {
        approvalRequest: null,
        approvalFeedback: null,
        approval: null
      }
    }
  },
  validations: function () {
    return {
      approval: {
        feedback: {
          required
        }
      }
    }
  },
  methods: {
    requestApproval: function () {
      let store = this.$store

      projectService.requestApproval(this.project.id)
        .then(() => {
          this.project.to_approve = true
          store.commit('success', 'success.request_approval')
          this.sendNotification('approvalRequest', 'success')
        }, () => {
          store.commit('error', 'errors.request_approval')
          this.sendNotification('approvalRequest', 'error')
        })
    },
    approve: function () {
      let store = this.$store

      projectService.approve(this.project.id)
        .then(() => {
          this.project.approved = true
          store.commit('success', 'success.approve')
          this.sendNotification('approval', 'success')
        }, () => {
          store.commit('error', 'errors.approve')
          this.sendNotification('approval', 'error')
        })
    },
    sendFeedback: function () {
      if (this.approval.feedback) {
        let store = this.$store

        projectService.sendFeedback(this.project.id, this.approval.feedback)
          .then(() => {
            let feedbackObjects = this.project.feedback
            if (!feedbackObjects) {
              feedbackObjects = []
            }
            let newFeedback = {
              created_date: new Date().toJSON().slice(0, 19).replace('T', ' '),
              feedback: this.approval.feedback,
              id: feedbackObjects.length > 0 ? (feedbackObjects[feedbackObjects.length - 1].id + 1) : 1,
              user_full_name: this.$store.state.userName,
              user_id: this.$store.state.userId
            }
            feedbackObjects.push(newFeedback)

            this.approval.feedback = null
            this.$v.approval.$reset()
            store.commit('success', 'success.feedback')
            this.sendNotification('approvalFeedback', 'success')
          }, () => {
            store.commit('error', 'errors.feedback')
            this.sendNotification('approvalFeedback', 'error')
          })
      }
    }
  },
  created: function () {
    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
  }
}
</script>

<style scoped>
  @media screen and (min-width: 901px) {
    .pt-20-r {
      padding-top: 20px !important;
    }
  }

  .card__scroll__small {
    max-height: 500px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .card__scroll__small {
    max-height: 250px;
  }
</style>



// WEBPACK FOOTER //
// src/components/project/Approval.vue